import { Space, Typography, Tag, Divider } from 'antd';
import type { ReactNode } from 'react';

const { Title, Text } = Typography;

export interface ModuleHeaderProps {
  /** Module/page title — already-localized. */
  title: string;
  /** Optional subtitle for context ("Finance / Invoices / IPC-042"). */
  subtitle?: string;
  /** Small chips like Sprint tags or role gates. Rendered to the right of the title. */
  chips?: Array<{ label: string; color?: string }>;
  /** Optional module icon (ReactNode from @ant-design/icons). */
  icon?: ReactNode;
  /** Primary actions (1–2 buttons expected). Right-aligned; auto-flips to left in RTL via `marginInlineStart: auto`. */
  actions?: ReactNode;
  /** Secondary helper row — filter summary, role scoping hint, etc. */
  meta?: ReactNode;
}

/**
 * Canonical page-top bar for every module / detail / list / wizard screen.
 *
 * RTL-aware: uses `marginInlineStart: 'auto'` for action alignment, so the
 * primary actions stay on the "end" side in both LTR and RTL. Never use
 * `marginLeft` / `marginRight` — always the logical-property variants.
 *
 * Contract:
 *  - `title` is the H3 page title
 *  - `subtitle` appears below as muted small-caps-ish context
 *  - `chips` are short non-actionable tags (e.g., "Sprint 1", "Role-gated")
 *  - `actions` ends the row on the inline-end side (RTL-aware)
 *  - `meta` is rendered on a second row below the divider (filters/role hint)
 */
export default function ModuleHeader({
  title,
  subtitle,
  chips,
  icon,
  actions,
  meta,
}: ModuleHeaderProps) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {icon && <span style={{ fontSize: 22, color: 'rgba(0,0,0,0.65)' }}>{icon}</span>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
            {title}
          </Title>
          {subtitle && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {subtitle}
            </Text>
          )}
        </div>
        {chips && chips.length > 0 && (
          <Space size={4} wrap>
            {chips.map((c, i) =>
              c.color ? (
                <Tag key={`${c.label}-${i}`} color={c.color}>
                  {c.label}
                </Tag>
              ) : (
                <Tag key={`${c.label}-${i}`}>{c.label}</Tag>
              ),
            )}
          </Space>
        )}
        {actions && <div style={{ marginInlineStart: 'auto' }}>{actions}</div>}
      </div>
      {meta && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div>{meta}</div>
        </>
      )}
    </div>
  );
}
